/* DrillInfoModal — shared drill info pop-down (build 215).
   ─────────────────────────────────────────────────────────
   Renders a drill's name, description, duration, category, difficulty
   and tags inside a Dialog, plus an optional "Watch in YouTube"
   external-handoff button.

   Replaces the previous in-modal `<iframe>` embed (builds 211–214). The
   embedded player kept throwing YouTube error 153 inside iOS WKWebView
   even after switching to the nocookie host — because Capacitor's
   default scheme (`capacitor://localhost`) doesn't match any origin
   YouTube's embed pipeline will accept. Rather than continue patching
   the iframe, build 215 removes it entirely. Coaches now tap
   "Watch in YouTube" and the URL opens via `@capacitor/browser`:
     • iOS  → SFSafariViewController (OS browser, slides up from bottom).
     • Android → Chrome Custom Tabs.
     • Web  → new tab.
   All three paths leave our WKWebView, matching the privacy policy's
   third-party processor disclosure for YouTube. The bottom-sheet feel
   of SFSafariViewController is what the user described as "feels in
   app" — the right boundary without an embedded WebView.

   This component is consumed by:
     • DrillLibrary.js          — opens on drill-card title/Watch tap.
     • PracticeChecklist.js     — opens on drill row name tap. */
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Clock, BookOpen, ExternalLink } from 'lucide-react';
import { DRILL_CATEGORIES } from '@/lib/drillTags';

/* Parse a YouTube video ID out of any of the URL shapes the drill
   library accepts (`youtu.be/<id>`, `youtube.com/watch?v=<id>`,
   `youtube.com/embed/<id>`, with or without a scheme). Returns the
   canonical `youtube.com/watch?v=<id>` URL on success, or null when
   the input isn't a recognisable YouTube link. The canonical watch
   URL is the right thing to hand to the system browser because iOS
   surfaces YouTube's smart-app banner on that page — one-tap handoff
   to the native YouTube app when installed. */
export function toWatchUrl(url) {
  if (!url || typeof url !== 'string') return null;
  let normalized = url.trim();
  if (!/^https?:\/\//i.test(normalized)) normalized = `https://${normalized}`;
  try {
    const u = new URL(normalized);
    let videoId = null;
    if (u.hostname.includes('youtu.be')) videoId = u.pathname.slice(1).split('/')[0];
    else if (u.hostname.includes('youtube.com')) {
      videoId = u.searchParams.get('v');
      if (!videoId && u.pathname.startsWith('/embed/')) videoId = u.pathname.split('/embed/')[1];
      if (!videoId && u.pathname.startsWith('/shorts/')) videoId = u.pathname.split('/shorts/')[1];
    }
    return videoId ? `https://www.youtube.com/watch?v=${videoId}` : null;
  } catch { return null; }
}

/* Hand off a URL to the system browser / native YouTube app. On
   native, `presentationStyle: 'popover'` triggers the slide-up
   sheet on iOS (the "feels in app" UX the user requested) while
   still being SFSafariViewController under the hood (not our
   embedded WKWebView). On web we fall back to `window.open`. */
export async function openInBrowser(watchUrl) {
  if (!watchUrl) return;
  try {
    if (Capacitor.isNativePlatform()) {
      await Browser.open({ url: watchUrl, presentationStyle: 'popover' });
    } else {
      window.open(watchUrl, '_blank', 'noopener,noreferrer');
    }
  } catch {
    try { window.open(watchUrl, '_blank', 'noopener,noreferrer'); } catch { /* ignore */ }
  }
}

/* Shared drill info modal.
   Props:
     • drill: { name, description, duration_minutes, category,
                difficulty, tags, video_url } | null
       When falsy the dialog is closed. Anything missing on the drill
       object is simply omitted from the render — duration/category/
       description/tags are all individually guarded.
     • onOpenChange(open): standard Radix Dialog open-change handler.
       The parent typically uses `(o) => !o && setActive(null)`. */
export default function DrillInfoModal({ drill, onOpenChange }) {
  const open = !!drill;
  const watchUrl = open ? toWatchUrl(drill?.video_url) : null;
  const catLabel = drill ? (DRILL_CATEGORIES.find(c => c.value === drill.category)?.label || drill.category) : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="drill-info-modal">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold uppercase tracking-tight" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
            {drill?.name || 'Drill'}
          </DialogTitle>
        </DialogHeader>
        {/* "Watch in YouTube" external handoff. Renders only when we
            can parse a YouTube video ID from `video_url`, so drills
            with no video have nothing to tap. */}
        {watchUrl && (
          <button
            type="button"
            onClick={() => openInBrowser(watchUrl)}
            data-testid="drill-watch-in-youtube"
            className="mt-1 inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full text-[12px] font-bold uppercase tracking-[.06em] transition-all active:scale-[.97] hover:opacity-90 self-start"
            style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              background: '#0B1824',
              color: '#fff',
              border: '1.5px solid #0B1824',
            }}
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Watch in YouTube
          </button>
        )}
        <div className="mt-3">
          <div className="flex items-center gap-3 text-[12px] mb-2" style={{ color: '#64748B' }}>
            {drill?.duration_minutes ? (
              <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{drill.duration_minutes}m</span>
            ) : null}
            {catLabel ? (
              <span className="inline-flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" />{catLabel}</span>
            ) : null}
            {drill?.difficulty && <span className="capitalize">{drill.difficulty}</span>}
          </div>
          {drill?.description && (
            <p className="text-[14px] leading-relaxed" style={{ color: '#334155' }}>{drill.description}</p>
          )}
          {drill?.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-3">
              {drill.tags.map((t, i) => (
                <span key={i} className="text-[11px] px-2 py-0.5 bg-amber-50 text-amber-700 rounded-full font-medium">{t}</span>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
